import { type DriveKind, driveKind } from "./api-types";

const DRIVE_NAMES: Record<DriveKind, string> = { document: "Google Doc", spreadsheet: "Google Sheet", presentation: "Google Slides", form: "Google Form", folder: "Google Drive folder", file: "Google Drive file" };

/** Plain text with web addresses in it: split into text and links, for showing notes and messages. */

export type TextPiece = { kind: "text"; text: string } | { kind: "link"; href: string; label: string };

/** Links people may open from a note: web pages, email, phone. Anything else (javascript:, data:) isn't a link. */
export function safeHref(href: string): string | null {
  const h = href.trim();
  if (/^(https?:\/\/|mailto:|tel:)/i.test(h)) return h;
  if (/^www\./i.test(h)) return `https://${h}`;
  return null;
}

/** A long address made readable: "example.com/path/to…" (no scheme, no query, at most `max` characters). */
export function shortUrl(href: string, max = 40): string {
  let shown = href;
  try {
    const u = new URL(href);
    if (u.protocol === "mailto:" || u.protocol === "tel:") return decodeURIComponent(u.pathname);
    // Google files say what they are; their addresses are long random ids.
    const kind = driveKind(href);
    if (kind) return DRIVE_NAMES[kind];
    shown = u.hostname.replace(/^www\./, "") + (u.pathname === "/" ? "" : decodeURIComponent(u.pathname));
    if (u.search || u.hash) shown += "…";
  } catch {
    // Not a full URL (www.something): show it as written.
  }
  shown = shown.replace(/…$/, "");
  const cut = shown.length > max;
  return cut || /[?#]/.test(href) ? `${shown.slice(0, max - 1).replace(/\/$/, "")}…` : shown;
}

const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
/** Trailing punctuation belongs to the sentence, not the address. */
function trimUrl(u: string): [string, string] {
  const m = /[.,;:!?)\]]+$/.exec(u);
  if (!m) return [u, ""];
  // Keep a closing paren that closes one inside the address (wikipedia-style).
  let tail = m[0];
  let url = u.slice(0, -tail.length);
  while (tail.startsWith(")") && (url.match(/\(/g)?.length ?? 0) > (url.match(/\)/g)?.length ?? 0)) {
    url += ")";
    tail = tail.slice(1);
  }
  return [url, tail];
}

function bareLinks(text: string): TextPiece[] {
  const out: TextPiece[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    if (m.index! < last) continue;
    let [url, tail] = trimUrl(m[1]!);
    // "(https://… with spaces …)": imported notes keep a link's address in parentheses, and some
    // addresses have unencoded spaces. Inside parentheses, the address runs to the closing one.
    if (text[m.index! - 1] === "(") {
      const close = text.indexOf(")", m.index!);
      const inside = close > 0 ? text.slice(m.index!, close) : "";
      if (inside.includes(" ") && !inside.includes("\n") && !inside.includes("(")) {
        url = inside;
        tail = "";
      }
    }
    const href = safeHref(url.replace(/ /g, "%20"));
    if (!href) continue;
    if (m.index! > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    out.push({ kind: "link", href, label: shortUrl(href) });
    last = m.index! + url.length;
    if (tail) {
      out.push({ kind: "text", text: tail });
      last += tail.length;
    }
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Splits text into plain text and links (shown short, opened in full). */
export function linkify(text: string): TextPiece[] {
  const out: TextPiece[] = [];
  for (const p of bareLinks(text)) {
    const prev = out[out.length - 1];
    if (p.kind === "text" && prev?.kind === "text") prev.text += p.text;
    else if (p.kind === "link" || p.text) out.push(p);
  }
  return out;
}
